import React, { useRef, Fragment, useMemo, useState } from "react";
import { UnfoldMore } from "@material-ui/icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import ClipboardTooltip from "../../EspIdTooltip";

const getIndicatorName = (name) => {
  const status = {
    CONFORMING: "conforming",
    "NON-CONFORMING": "nonconformimg",
    "INVALID 340B ID": "invalid-340b-id",
    "NOT A CONTRACT PHARMACY": "not-a-contract-pharmacy",
  };

  return status[name];
};

export default function MedicalClaimsTable(props) {
  const tableContainerRef = useRef();

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("esp_id", {
      header: "ESP ID",
      enableSorting: false,
      cell: ({ renderValue }) => <ClipboardTooltip value={renderValue()} />,
    }),
    columnHelper.accessor("manufacturer", {
      header: "Mfr",
      enableSorting: false,
      cell: ({ renderValue }) => <div className="t--sm t--500">{renderValue()}</div>,
    }),
    columnHelper.accessor((row) => row.ndc, {
      header: "NDC-11",
      enableSorting: false,
      cell: ({ row, getValue }) => {
        return (
          <div>
            <div className="t--sm t--500">{row.original.product_name}</div>
            <div className="t--sm t--subtitle">{getValue()}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor("quantity", {
      header: "Quantity",
      cell: ({ renderValue }) => <div className="t--md t--500">{renderValue()}</div>,
    }),
    columnHelper.accessor("service_provider_id", {
      header: "Service Provider NPI",
      enableSorting: false,
    }),
    columnHelper.accessor("id_340b", {
      header: "340B ID",
      enableSorting: false,
      cell: ({ renderValue }) => <div className="t--sm t--500">{renderValue()}</div>,
    }),
    // TODO: update when we have valid/invalid medical claims
    // columnHelper.accessor((row) => row.status, {
    //   header: "Status",
    //   enableSorting: false,
    //   cell: ({ getValue }) => {
    //     return (
    //       <div className="badge">
    //         <div className={`badge__indicator badge__indicator--${getIndicatorName(getValue())}`} />
    //         {getValue()}
    //       </div>
    //     );
    //   },
    // }),
    columnHelper.accessor("submission_date", {
      header: "Date Submitted",
      cell: ({ renderValue }) => <div className="t--sm t--500">{renderValue()}</div>,
    }),
  ];

  const claims = useMemo(() => props.claims, [props.claims]);

  const table = useReactTable({
    data: claims,
    columns,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const renderVirtualRows = () => {
    return virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];

      return (
        <tr
          key={row.id}
          className="sm"
          ref={rowVirtualizer.measureElement}
          data-index={virtualRow.index}
        >
          {row.getVisibleCells().map((cell) => {
            return (
              <td key={cell.id} className="t--sm">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  // Investigate why this is needed?
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  const [sortState, setSortState] = useState({});

  const sort = (column) => {
    const nextSortingOrder = sortState[column.id] === "asc" ? "desc" : "asc";
    setSortState({ ...sortState, [column.id]: nextSortingOrder });
    props.handleSort(column.id, nextSortingOrder);
  };

  return (
    <>
      <div ref={tableContainerRef} className="virtual__table__reports__container">
        <table className="primary scrollable">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="sm">
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        style={{ fontSize: 12, display: "inline-flex", verticalAlign: "top" }}
                        onClick={() => sort(header.column)}
                      >
                        <UnfoldMore fontSize="inherit" color="inherit" />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {renderVirtualRows()}
            {paddingBottom > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
